<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8 flex-wrap" style="gap: 16px">
      <div>
        <h1 class="page-title">Alle Sections</h1>
        <p class="page-subtitle">Verwalten Sie Ihre Shopify Sections</p>
      </div>
      <button v-if="$can('sections.create')" class="primary-btn" @click="$router.push('/section/create')">
        <v-icon size="16">mdi-plus</v-icon>
        <span>Section anlegen</span>
      </button>
    </div>

    <!-- Filter Bar -->
    <div class="filter-bar mb-6">
      <div class="filter-input">
        <v-icon size="16" color="#888" class="filter-icon">mdi-magnify</v-icon>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Section durchsuchen…"
          class="filter-native"
        />
        <v-icon
          v-if="searchQuery"
          size="14"
          color="#aaa"
          style="cursor: pointer"
          @click="searchQuery = ''"
        >mdi-close-circle</v-icon>
      </div>

      <div class="combo-wrap" ref="categoryDropdown">
        <button
          type="button"
          class="iw iw-trigger"
          :class="{ 'iw-f': categoryMenuOpen }"
          @click="categoryMenuOpen = !categoryMenuOpen"
        >
          <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-tag-outline</v-icon>
          <span class="ii-val">{{ filterCategory || 'Alle Kategorien' }}</span>
          <v-icon size="14" color="#888" :class="{ 'chev-open': categoryMenuOpen }">mdi-chevron-down</v-icon>
        </button>
        <div v-if="categoryMenuOpen" class="combo-menu">
          <button type="button" class="combo-option" @click="selectCategory(null)">
            <span class="combo-label">Alle Kategorien</span>
            <v-icon v-if="filterCategory === null" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
          </button>
          <div v-if="categories.length" class="combo-divider"></div>
          <button
            v-for="cat in categories"
            :key="cat"
            type="button"
            class="combo-option"
            @click="selectCategory(cat)"
          >
            <span class="combo-label">{{ cat }}</span>
            <v-icon v-if="filterCategory === cat" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
          </button>
        </div>
      </div>

      <div class="combo-wrap" ref="priceDropdown">
        <button
          type="button"
          class="iw iw-trigger"
          :class="{ 'iw-f': priceMenuOpen }"
          @click="priceMenuOpen = !priceMenuOpen"
        >
          <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-currency-eur</v-icon>
          <span class="ii-val">{{ currentPriceLabel }}</span>
          <v-icon size="14" color="#888" :class="{ 'chev-open': priceMenuOpen }">mdi-chevron-down</v-icon>
        </button>
        <div v-if="priceMenuOpen" class="combo-menu">
          <button
            v-for="opt in priceOptions"
            :key="opt.value ?? 'all'"
            type="button"
            class="combo-option"
            @click="selectPrice(opt.value)"
          >
            <span class="combo-label">{{ opt.label }}</span>
            <v-icon v-if="filterPrice === opt.value" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
          </button>
        </div>
      </div>

      <div class="combo-wrap" ref="statusDropdown">
        <button
          type="button"
          class="iw iw-trigger"
          :class="{ 'iw-f': statusMenuOpen }"
          @click="statusMenuOpen = !statusMenuOpen"
        >
          <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-progress-check</v-icon>
          <span class="ii-val">{{ currentStatusLabel }}</span>
          <v-icon size="14" color="#888" :class="{ 'chev-open': statusMenuOpen }">mdi-chevron-down</v-icon>
        </button>
        <div v-if="statusMenuOpen" class="combo-menu">
          <button
            v-for="opt in statusOptions"
            :key="opt.value ?? 'all-status'"
            type="button"
            class="combo-option"
            @click="selectStatus(opt.value)"
          >
            <span class="combo-label">{{ opt.label }}</span>
            <v-icon v-if="filterStatus === opt.value" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
          </button>
        </div>
      </div>

      <div class="combo-wrap" ref="channelDropdown">
        <button
          type="button"
          class="iw iw-trigger"
          :class="{ 'iw-f': channelMenuOpen }"
          @click="channelMenuOpen = !channelMenuOpen"
        >
          <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-store-outline</v-icon>
          <span class="ii-val">{{ currentChannelLabel }}</span>
          <v-icon size="14" color="#888" :class="{ 'chev-open': channelMenuOpen }">mdi-chevron-down</v-icon>
        </button>
        <div v-if="channelMenuOpen" class="combo-menu">
          <button
            v-for="opt in channelOptions"
            :key="opt.value ?? 'all-channel'"
            type="button"
            class="combo-option"
            @click="selectChannel(opt.value)"
          >
            <span class="combo-label">{{ opt.label }}</span>
            <v-icon v-if="filterChannel === opt.value" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
          </button>
        </div>
      </div>

      <div class="combo-wrap" ref="purposeDropdown">
        <button
          type="button"
          class="iw iw-trigger"
          :class="{ 'iw-f': purposeMenuOpen }"
          @click="purposeMenuOpen = !purposeMenuOpen"
        >
          <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-label-outline</v-icon>
          <span class="ii-val">{{ currentPurposeLabel }}</span>
          <v-icon size="14" color="#888" :class="{ 'chev-open': purposeMenuOpen }">mdi-chevron-down</v-icon>
        </button>
        <div v-if="purposeMenuOpen" class="combo-menu combo-menu-wide">
          <button
            v-for="opt in purposeOptions"
            :key="opt.value ?? 'all-purpose'"
            type="button"
            class="combo-option combo-option-2line"
            @click="selectPurpose(opt.value)"
          >
            <div class="combo-text">
              <span class="combo-label">{{ opt.label }}</span>
              <span v-if="opt.desc" class="combo-desc">{{ opt.desc }}</span>
            </div>
            <v-icon v-if="filterPurpose === opt.value" size="14" color="#2E7D32" class="combo-check">mdi-check</v-icon>
          </button>
        </div>
      </div>

      <div class="view-toggle">
        <button
          class="view-btn"
          :class="{ active: viewMode === 'grid' }"
          title="Grid-Ansicht"
          @click="setViewMode('grid')"
        >
          <v-icon size="16">mdi-view-grid-outline</v-icon>
        </button>
        <button
          class="view-btn"
          :class="{ active: viewMode === 'list' }"
          title="Listen-Ansicht"
          @click="setViewMode('list')"
        >
          <v-icon size="16">mdi-format-list-bulleted</v-icon>
        </button>
      </div>
    </div>

    <!-- Result count -->
    <div v-if="!loading && sections.length > 0" class="result-count mb-4">
      {{ filteredSections.length }}
      {{ filteredSections.length === 1 ? 'Section gefunden' : 'Sections gefunden' }}
    </div>

    <!-- Loading skeletons (grid) -->
    <div v-if="loading && viewMode === 'grid'" class="grid-sections">
      <div v-for="i in 8" :key="`skel-${i}`" class="section-card">
        <div class="skel skel-image"></div>
        <div class="pa-4">
          <div class="skel" style="height: 14px; width: 70%; margin-bottom: 8px"></div>
          <div class="skel" style="height: 10px; width: 40%; margin-bottom: 16px"></div>
          <div class="skel" style="height: 12px; width: 100%"></div>
        </div>
      </div>
    </div>

    <!-- Loading skeletons (list) -->
    <div v-else-if="loading && viewMode === 'list'" class="list-wrap">
      <div v-for="i in 6" :key="`skel-list-${i}`" class="list-row">
        <div class="skel" style="width: 56px; height: 56px; border-radius: 10px"></div>
        <div style="flex: 1; padding: 0 16px">
          <div class="skel" style="height: 13px; width: 40%; margin-bottom: 8px"></div>
          <div class="skel" style="height: 10px; width: 25%"></div>
        </div>
        <div class="skel" style="width: 80px; height: 12px"></div>
      </div>
    </div>

    <!-- Empty: no sections at all -->
    <div v-else-if="sections.length === 0" class="empty-state">
      <v-icon size="48" color="#c5cbd3" class="mb-4">mdi-vector-intersection</v-icon>
      <h3 class="empty-title">Noch keine Sections vorhanden</h3>
      <p class="empty-desc">Erstellen Sie Ihre erste Section, um loszulegen.</p>
      <button v-if="$can('sections.create')" class="primary-btn mt-4" @click="$router.push('/section/create')">
        <v-icon size="16">mdi-plus</v-icon>
        Erste Section anlegen
      </button>
    </div>

    <!-- Empty: no results after filtering -->
    <div v-else-if="filteredSections.length === 0" class="empty-state">
      <v-icon size="48" color="#c5cbd3" class="mb-4">mdi-magnify</v-icon>
      <h3 class="empty-title">Keine Sections gefunden</h3>
      <p class="empty-desc">Versuchen Sie andere Suchbegriffe oder Filter.</p>
      <button class="secondary-btn mt-4" @click="resetFilters">
        <v-icon size="16">mdi-refresh</v-icon>
        Filter zurücksetzen
      </button>
    </div>

    <!-- Grid View -->
    <div v-else-if="viewMode === 'grid'" class="grid-sections">
      <div
        v-for="section in filteredSections"
        :key="section.id"
        class="section-card"
        @click="$router.push(`/section/${section.id}`)"
      >
        <div class="card-image-wrap">
          <v-img
            v-if="section.hasImage"
            :src="sectionImageUrl(section)"
            cover
            aspect-ratio="1"
          >
            <template #placeholder>
              <div class="image-placeholder">
                <v-progress-circular indeterminate size="22" width="2" color="#c5cbd3" />
              </div>
            </template>
            <template #error>
              <div class="image-placeholder">
                <v-icon size="28" color="#c5cbd3">mdi-image-off-outline</v-icon>
              </div>
            </template>
          </v-img>
          <div v-else class="image-placeholder" style="aspect-ratio: 1">
            <v-icon size="28" color="#c5cbd3">mdi-image-off-outline</v-icon>
          </div>

          <div
            v-if="section.isFree == 0"
            class="premium-star"
            title="Premium-Section"
          >
            <v-icon size="14" color="#E65100">mdi-star</v-icon>
          </div>
        </div>

        <div class="pa-4">
          <div class="card-title text-truncate">{{ section.title }}</div>
          <div class="card-handle text-truncate">{{ section.handle }}</div>

          <div class="card-badge-row">
            <span v-if="section.status === 'draft'" class="card-badge badge-draft">
              <v-icon size="11">mdi-file-document-edit-outline</v-icon>
              Entwurf
            </span>
            <span v-else class="card-badge badge-completed">
              <v-icon size="11">mdi-check-circle-outline</v-icon>
              Fertiggestellt
            </span>

            <span v-if="hasShopifyAppChannel(section)" class="card-badge badge-app" title="In der Shopify App verfügbar">
              <v-icon size="11">mdi-shopping-outline</v-icon>
              App
            </span>
          </div>

          <div class="card-info">
            <div class="info-cell">
              <div class="info-label">Preis</div>
              <div class="info-value">
                {{ section.isFree == 1 ? 'Kostenlos' : formatCurrency(section.price) }}
              </div>
            </div>
            <div class="info-divider"></div>
            <div class="info-cell">
              <div class="info-label">Kategorie</div>
              <div class="info-value">{{ section.categoryName || '—' }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div v-else class="list-wrap">
      <div class="list-header">
        <div class="list-col-image"></div>
        <div class="list-col-title">Section</div>
        <div class="list-col-category">Kategorie</div>
        <div class="list-col-price">Preis</div>
        <div class="list-col-status">Status</div>
        <div class="list-col-chev"></div>
      </div>

      <div
        v-for="section in filteredSections"
        :key="section.id"
        class="list-row list-row-item"
        @click="$router.push(`/section/${section.id}`)"
      >
        <div class="list-col-image">
          <div class="list-thumb">
            <v-img
              v-if="section.hasImage"
              :src="sectionImageUrl(section)"
              cover
              width="44"
              height="44"
            >
              <template #error>
                <div class="list-thumb-fallback"><v-icon size="18" color="#c5cbd3">mdi-image-off-outline</v-icon></div>
              </template>
            </v-img>
            <div v-else class="list-thumb-fallback"><v-icon size="18" color="#c5cbd3">mdi-image-off-outline</v-icon></div>
          </div>
        </div>
        <div class="list-col-title">
          <div class="list-title">{{ section.title }}</div>
          <div class="list-handle">{{ section.handle }}</div>
        </div>
        <div class="list-col-category">{{ section.categoryName || '—' }}</div>
        <div class="list-col-price">
          {{ section.isFree == 1 ? 'Kostenlos' : formatCurrency(section.price) }}
        </div>
        <div class="list-col-status">
          <span v-if="section.status === 'draft'" class="list-pill pill-draft">
            <v-icon size="10">mdi-file-document-edit-outline</v-icon>Entwurf
          </span>
          <span v-else class="list-pill pill-completed">
            <v-icon size="10">mdi-check-circle-outline</v-icon>Fertig
          </span>
          <span v-if="hasShopifyAppChannel(section)" class="list-pill pill-app" style="margin-left: 4px">
            <v-icon size="10">mdi-shopping-outline</v-icon>App
          </span>
        </div>
        <div class="list-col-chev">
          <v-icon size="16" color="#c5cbd3">mdi-chevron-right</v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SectionOverview',

  data: () => ({
    sections: [],
    loading: false,
    searchQuery: '',
    filterCategory: null,
    filterPrice: null,
    filterStatus: null,
    filterChannel: null,
    filterPurpose: null,
    viewMode: localStorage.getItem('sections.viewMode') === 'list' ? 'list' : 'grid',
    categoryMenuOpen: false,
    priceMenuOpen: false,
    statusMenuOpen: false,
    channelMenuOpen: false,
    purposeMenuOpen: false,
    priceOptions: [
      { label: 'Alle Preise',  value: null },
      { label: 'Kostenlos',    value: 'free' },
      { label: 'Premium',      value: 'premium' },
    ],
    statusOptions: [
      { label: 'Alle Status',     value: null },
      { label: 'Entwurf',         value: 'draft' },
      { label: 'Fertiggestellt',  value: 'completed' },
    ],
    channelOptions: [
      { label: 'Alle Kanäle',         value: null },
      { label: 'In Shopify App',      value: 'shopify_app' },
      { label: 'Ohne Vertriebskanal', value: 'none' },
    ],
    purposeOptions: [
      { label: 'Alle Tags',     value: null },
      { label: 'Ohne Tag',      value: 'none' },
      { label: 'Intern',        value: 'intern',     desc: 'Nur für Tests' },
      { label: 'Intern & App',  value: 'intern_app', desc: 'Tests + Verkauf in App' },
      { label: 'App',           value: 'app',        desc: 'Nur in Shopify App' },
    ],
  }),

  computed: {
    categories() {
      return [
        ...new Set(this.sections.filter((s) => s.categoryName).map((s) => s.categoryName)),
      ].sort();
    },
    currentPriceLabel() {
      return this.priceOptions.find((o) => o.value === this.filterPrice)?.label || 'Alle Preise';
    },
    currentStatusLabel() {
      return this.statusOptions.find((o) => o.value === this.filterStatus)?.label || 'Alle Status';
    },
    currentChannelLabel() {
      return this.channelOptions.find((o) => o.value === this.filterChannel)?.label || 'Alle Kanäle';
    },
    currentPurposeLabel() {
      return this.purposeOptions.find((o) => o.value === this.filterPurpose)?.label || 'Alle Tags';
    },
    filteredSections() {
      let filtered = [...this.sections];

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (s) =>
            s.title?.toLowerCase().includes(q) ||
            s.handle?.toLowerCase().includes(q) ||
            s.categoryName?.toLowerCase().includes(q)
        );
      }

      if (this.filterCategory) {
        filtered = filtered.filter((s) => s.categoryName === this.filterCategory);
      }

      if (this.filterPrice === 'free') {
        filtered = filtered.filter((s) => s.isFree == 1);
      } else if (this.filterPrice === 'premium') {
        filtered = filtered.filter((s) => s.isFree == 0);
      }

      if (this.filterStatus) {
        filtered = filtered.filter((s) => s.status === this.filterStatus);
      }

      if (this.filterChannel === 'shopify_app') {
        filtered = filtered.filter((s) => this.hasShopifyAppChannel(s));
      } else if (this.filterChannel === 'none') {
        filtered = filtered.filter((s) => !s.salesChannels || s.salesChannels.length === 0);
      }

      if (this.filterPurpose === 'none') {
        filtered = filtered.filter((s) => !s.purpose);
      } else if (this.filterPurpose) {
        filtered = filtered.filter((s) => s.purpose === this.filterPurpose);
      }

      return filtered;
    },
  },

  mounted() {
    this.getAllSections();
    document.addEventListener('click', this.handleOutsideClick);
  },
  activated() { this.getAllSections(); },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    async getAllSections() {
      this.loading = true;
      try {
        const result = await axios.get('/api/sections/get');
        this.sections = result.data;
      } catch (e) {
        console.error('Fehler beim Laden!', e);
      } finally {
        this.loading = false;
      }
    },
    hasShopifyAppChannel(s) {
      return Array.isArray(s.salesChannels) && s.salesChannels.includes('shopify_app');
    },
    resetFilters() {
      this.searchQuery = '';
      this.filterCategory = null;
      this.filterPrice = null;
      this.filterStatus = null;
      this.filterChannel = null;
      this.filterPurpose = null;
    },
    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem('sections.viewMode', mode);
    },
    selectCategory(value) {
      this.filterCategory = value;
      this.categoryMenuOpen = false;
    },
    selectPrice(value) {
      this.filterPrice = value;
      this.priceMenuOpen = false;
    },
    selectStatus(value) {
      this.filterStatus = value;
      this.statusMenuOpen = false;
    },
    selectChannel(value) {
      this.filterChannel = value;
      this.channelMenuOpen = false;
    },
    selectPurpose(value) {
      this.filterPurpose = value;
      this.purposeMenuOpen = false;
    },
    handleOutsideClick(e) {
      if (this.categoryMenuOpen) {
        const el = this.$refs.categoryDropdown;
        if (el && !el.contains(e.target)) this.categoryMenuOpen = false;
      }
      if (this.priceMenuOpen) {
        const el = this.$refs.priceDropdown;
        if (el && !el.contains(e.target)) this.priceMenuOpen = false;
      }
      if (this.statusMenuOpen) {
        const el = this.$refs.statusDropdown;
        if (el && !el.contains(e.target)) this.statusMenuOpen = false;
      }
      if (this.channelMenuOpen) {
        const el = this.$refs.channelDropdown;
        if (el && !el.contains(e.target)) this.channelMenuOpen = false;
      }
      if (this.purposeMenuOpen) {
        const el = this.$refs.purposeDropdown;
        if (el && !el.contains(e.target)) this.purposeMenuOpen = false;
      }
    },
    sectionImageUrl(section) {
      const v = section.updatedAt ? new Date(section.updatedAt).getTime() : 0;
      return `/api/sections/${section.id}/image?v=${v}`;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(value || 0);
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; margin-bottom: 6px; }
.page-subtitle { font-size: 13px; color: #888; }

/* Buttons */
.primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #0a1628;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}
.primary-btn:hover { background: #1d3457; }

.secondary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: white;
  color: #0a1628;
  border: 1px solid #eceef2;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s;
}
.secondary-btn:hover { border-color: #0a1628; }

/* Filter bar */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 10px;
}
.filter-bar .filter-input { flex: 2 1 240px; }
.filter-bar .combo-wrap   { flex: 1 1 160px; min-width: 160px; }
.filter-bar .view-toggle  { flex: 0 0 auto; margin-left: auto; }

/* View Toggle */
.view-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  background: white;
  border: 1px solid #eceef2;
  border-radius: 10px;
  height: 44px;
}
.view-btn {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  color: #888;
  transition: all 0.15s;
}
.view-btn:hover:not(.active) { background: #f8f9fb; color: #0a1628; }
.view-btn.active { background: #0a1628; color: white; }
.view-btn.active :deep(.v-icon) { color: white !important; }
.filter-input {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  height: 44px;
  background: white;
  border: 1px solid #eceef2;
  border-radius: 10px;
  transition: border-color 0.15s;
}
.filter-input:focus-within { border-color: #0a1628; }
.filter-icon { flex-shrink: 0; }
.filter-native {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #0a1628;
  font-weight: 500;
  font-family: inherit;
}
.filter-native::placeholder { color: #aaa; }

/* Custom Dropdown (einheitliches Pattern) */
.combo-wrap { position: relative; }
.iw {
  display: flex; align-items: center; gap: 10px;
  height: 44px; padding: 0 14px; width: 100%;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw-trigger {
  cursor: pointer; text-align: left; font-family: inherit;
}
.iw-trigger:hover:not(.iw-f) { border-color: #c5cbd3; }
.iw.iw-f {
  border-color: #0a1628; box-shadow: 0 0 0 3px rgba(10,22,40,0.04);
}
.ii-val {
  flex: 1; font-size: 13px; color: #0a1628; font-weight: 500;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.chev-open { transform: rotate(180deg); transition: transform 0.2s; }

.combo-menu {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  box-shadow: 0 12px 32px rgba(10,22,40,0.12);
  z-index: 50; max-height: 280px; overflow-y: auto;
  padding: 6px; animation: comboFade 0.12s ease;
}
@keyframes comboFade {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.combo-option {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 9px 10px; background: transparent; border: none;
  border-radius: 7px; cursor: pointer; transition: background 0.1s;
  text-align: left; font-family: inherit;
}
.combo-option:hover { background: #f8f9fb; }
.combo-label { flex: 1; font-size: 14px; color: #0a1628; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.combo-divider { height: 1px; background: #eceef2; margin: 4px 0; }

/* Erweiterte Variante: 2-zeilig (Label + Beschreibung darunter) */
.combo-menu-wide { min-width: 240px; }
.combo-option-2line { gap: 8px; align-items: flex-start; padding: 8px 10px; }
.combo-option-2line .combo-text {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 1px;
}
.combo-option-2line .combo-label { font-size: 13.5px; line-height: 1.3; }
.combo-option-2line .combo-desc {
  font-size: 11px; color: #999; font-weight: 400; line-height: 1.35;
}
.combo-option-2line .combo-check { margin-top: 4px; flex-shrink: 0; }

.result-count { font-size: 12px; color: #888; font-weight: 500; }

/* Grid */
.grid-sections {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.section-card {
  background: white;
  border: 1px solid #eceef2;
  border-radius: 14px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.15s ease;
}
.section-card:hover {
  border-color: #0a1628;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(10, 22, 40, 0.06);
}

.card-image-wrap { position: relative; background: #f8f9fb; }
.image-placeholder {
  width: 100%;
  height: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fb;
}

.card-badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}
.card-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  white-space: nowrap;
}
.badge-draft     { background: #f0f2f5; color: #666; border: 1px solid #eceef2; }
.badge-completed { background: rgba(46,125,50,0.1); color: #2E7D32; border: 1px solid rgba(46,125,50,0.2); }
.badge-app       { background: rgba(10,22,40,0.06); color: #0a1628; border: 1px solid rgba(10,22,40,0.12); }

.premium-star {
  position: absolute;
  top: 10px;
  left: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 152, 0, 0.3);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(8px);
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #0a1628;
  margin-bottom: 2px;
}
.card-handle {
  font-size: 11px;
  color: #999;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  margin-bottom: 10px;
}

.card-info { display: flex; align-items: center; gap: 14px; padding-top: 12px; border-top: 1px solid #f5f5f5; }
.info-cell { flex: 1; min-width: 0; }
.info-label { font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.3px; font-weight: 600; margin-bottom: 3px; }
.info-value { font-size: 13px; font-weight: 600; color: #0a1628; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.info-divider { width: 1px; height: 26px; background: #eceef2; }

/* List View */
.list-wrap {
  background: white;
  border: 1px solid #eceef2;
  border-radius: 14px;
  overflow: hidden;
}
.list-header {
  display: grid;
  grid-template-columns: 72px minmax(0, 2fr) minmax(0, 1fr) 110px 110px 32px;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  background: #fafbfc;
  border-bottom: 1px solid #eceef2;
  font-size: 11px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.list-row {
  display: grid;
  grid-template-columns: 72px minmax(0, 2fr) minmax(0, 1fr) 110px 110px 32px;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.15s;
}
.list-row:last-child { border-bottom: none; }
.list-row-item { cursor: pointer; }
.list-row-item:hover { background: #fafbfc; }

.list-col-image { display: flex; align-items: center; }
.list-thumb {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  overflow: hidden;
  background: #f8f9fb;
  border: 1px solid #eceef2;
}
.list-thumb-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.list-col-title { min-width: 0; }
.list-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #0a1628;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 2px;
}
.list-handle {
  font-size: 11px;
  color: #999;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.list-col-category {
  font-size: 13px;
  color: #555;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.list-col-price {
  font-size: 13px;
  font-weight: 600;
  color: #0a1628;
  font-variant-numeric: tabular-nums;
}

.list-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}
.pill-draft { background: #f0f2f5; color: #666; }
.pill-completed { background: rgba(46,125,50,0.1); color: #2E7D32; }
.pill-app { background: rgba(10,22,40,0.9); color: white; }
.pill-premium { background: rgba(255,152,0,0.1); color: #E65100; }
.pill-free { background: rgba(46,125,50,0.1); color: #2E7D32; }

.list-col-chev { display: flex; align-items: center; justify-content: flex-end; }

/* Empty state */
.empty-state {
  background: white;
  border: 1px solid #eceef2;
  border-radius: 16px;
  padding: 64px 32px;
  text-align: center;
}
.empty-title { font-size: 16px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.empty-desc { font-size: 13px; color: #888; }

/* Skeletons */
.skel { background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 6px; }
.skel-image { aspect-ratio: 1; border-radius: 0; }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

.text-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

@media (max-width: 1200px) {
  .filter-bar .filter-input { flex: 1 1 100%; }
}
@media (max-width: 900px) {
  .filter-bar .combo-wrap { flex: 1 1 calc(50% - 5px); min-width: 0; }
  .filter-bar .view-toggle { margin-left: 0; }
  .list-header, .list-row { grid-template-columns: 56px minmax(0, 1fr) 90px 24px; }
  .list-header .list-col-category, .list-header .list-col-price,
  .list-row .list-col-category, .list-row .list-col-price { display: none; }
}

@media (max-width: 560px) {
  .filter-bar { grid-template-columns: 1fr; }
}
</style>
